import classNames from "classnames"
import { FormEvent, KeyboardEvent, useId, useState } from "react"
import { useTranslation } from "react-i18next"

import styles from "./TrackingBlock.module.scss"

type TrackingTabId = "order" | "tracking"

export interface TrackingBlockProps {
  /** 移动端预览等场景：去掉卡片外边框 */
  hideCardBorder?: boolean
  isMobile?: boolean
}

const TITLE = "Track Your Order"
const TAB_ORDER_NUMBER = "Order Number"
const TAB_TRACKING_NUMBER = "Tracking Number"
const LABEL_EMAIL_OR_PHONE = "Email or Phone Number"
const BUTTON_TRACK = "Track"

const TrackingBlock = ({ hideCardBorder = false, isMobile = false }: TrackingBlockProps) => {
  const { t } = useTranslation("tracking_page")
  const baseId = useId()
  const tabOrderId = `${baseId}-tab-order`
  const tabTrackingId = `${baseId}-tab-tracking`
  const panelOrderId = `${baseId}-panel-order`
  const panelTrackingId = `${baseId}-panel-tracking`

  const [activeTab, setActiveTab] = useState<TrackingTabId>("order")
  const [orderNumber, setOrderNumber] = useState("")
  const [emailOrPhone, setEmailOrPhone] = useState("")
  const [trackingNumber, setTrackingNumber] = useState("")

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
  }

  const handleOrderTabKeyDown = (event: KeyboardEvent<HTMLButtonElement>) => {
    if (event.key !== "ArrowRight") {
      return
    }
    event.preventDefault()
    setActiveTab("tracking")
  }

  const handleTrackingTabKeyDown = (event: KeyboardEvent<HTMLButtonElement>) => {
    if (event.key !== "ArrowLeft") {
      return
    }
    event.preventDefault()
    setActiveTab("order")
  }

  return (
    <div className={styles.root}>
      <div className={styles.cardWrap}>
        <div className={classNames(styles.card, hideCardBorder && styles.cardBorderless, isMobile ? styles.mobileCard : undefined)}>
          <h2 className={styles.title}>{TITLE}</h2>

          <div className={styles.tabList} role="tablist" aria-label={TITLE}>
            <button
              type="button"
              role="tab"
              id={tabOrderId}
              aria-selected={activeTab === "order"}
              aria-controls={panelOrderId}
              tabIndex={activeTab === "order" ? 0 : -1}
              className={`${styles.tab} ${activeTab === "order" ? styles.tabActive : ""}`}
              onClick={() => {
                setActiveTab("order")
              }}
              onKeyDown={handleOrderTabKeyDown}
            >
              {TAB_ORDER_NUMBER}
            </button>
            <button
              type="button"
              role="tab"
              id={tabTrackingId}
              aria-selected={activeTab === "tracking"}
              aria-controls={panelTrackingId}
              tabIndex={activeTab === "tracking" ? 0 : -1}
              className={`${styles.tab} ${activeTab === "tracking" ? styles.tabActive : ""}`}
              onClick={() => {
                setActiveTab("tracking")
              }}
              onKeyDown={handleTrackingTabKeyDown}
            >
              {TAB_TRACKING_NUMBER}
            </button>
          </div>

          <form className={styles.form} onSubmit={handleSubmit} noValidate>
            <div
              id={panelOrderId}
              className={activeTab === "order" ? styles.fields : styles.hidden}
              role="tabpanel"
              aria-labelledby={tabOrderId}
              hidden={activeTab !== "order"}
            >
              <div className={styles.field}>
                <label className={styles.label} htmlFor={`${baseId}-order-number`}>
                  {TAB_ORDER_NUMBER}
                </label>
                <input
                  id={`${baseId}-order-number`}
                  className={styles.input}
                  name="orderNumber"
                  type="text"
                  autoComplete="off"
                  value={orderNumber}
                  onChange={(event) => {
                    setOrderNumber(event.target.value)
                  }}
                />
              </div>
              <div className={styles.field}>
                <label className={styles.label} htmlFor={`${baseId}-email-phone`}>
                  {LABEL_EMAIL_OR_PHONE}
                </label>
                <input
                  id={`${baseId}-email-phone`}
                  className={styles.input}
                  name="emailOrPhone"
                  type="text"
                  autoComplete="email"
                  value={emailOrPhone}
                  onChange={(event) => {
                    setEmailOrPhone(event.target.value)
                  }}
                />
              </div>
            </div>

            <div
              id={panelTrackingId}
              className={activeTab === "tracking" ? styles.fields : styles.hidden}
              role="tabpanel"
              aria-labelledby={tabTrackingId}
              hidden={activeTab !== "tracking"}
            >
              <div className={styles.field}>
                <label className={styles.label} htmlFor={`${baseId}-tracking-number`}>
                  {TAB_TRACKING_NUMBER}
                </label>
                <input
                  id={`${baseId}-tracking-number`}
                  className={styles.input}
                  name="trackingNumber"
                  type="text"
                  autoComplete="off"
                  value={trackingNumber}
                  onChange={(event) => {
                    setTrackingNumber(event.target.value)
                  }}
                />
              </div>
            </div>

            <div className={styles.footer}>
              <button type="submit" className={styles.trackButton}>
                {BUTTON_TRACK}
              </button>
            </div>
          </form>
        </div>
        <div className={styles.previewMask} aria-hidden="true">
          <div className={styles.previewMaskBlur} />
          <div className={styles.previewMaskHoverLayer}>
            <div className={styles.previewMaskDim} />
            <p className={styles.previewMaskText}>
              {t("trackingOrderLookupPreviewOverlay")}
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}

export default TrackingBlock
